// frontend/src/pages/purchase/PurchaseOrderDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { getSuppliers } from '../../utils/supplierApi';

const PurchaseOrderDetail = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [po, setPo] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [uuid]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 伝票情報と仕入先マスターを並行して取得
      const [poRes, supplierData] = await Promise.all([
        apiFetch(`/purchase-orders/${uuid}`),
        getSuppliers()
      ]);

      if (!poRes.ok) throw new Error('伝票が見つかりません');

      const poData = await poRes.json();
      setPo(poData);
      setSuppliers(supplierData);
    } catch (e) {
      alert(e.message);
      navigate('/purchase/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  const getSupplierName = (suppUuid) => {
    const s = suppliers.find(x => x.uuid === suppUuid);
    return s ? `[${s.supplier_code || '--'}] ${s.name}` : '不明な仕入先';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft': return <span className="badge bg-secondary px-3 py-2 fs-6">下書き</span>;
      case 'ordered': return <span className="badge bg-primary px-3 py-2 fs-6">発注済</span>;
      case 'completed': return <span className="badge bg-success px-3 py-2 fs-6">入庫完了</span>;
      case 'cancelled': return <span className="badge bg-danger px-3 py-2 fs-6">キャンセル</span>;
      default: return <span className="badge bg-light text-dark px-3 py-2 fs-6">{status}</span>;
    }
  };

  if (loading || !po) return <div className="p-5 text-center text-muted"><i className="fa fa-spinner fa-spin fa-2x mb-3"></i><br />伝票データを読み込み中...</div>;

  return (
    <div className="pb-5">
      <div className="d-flex align-items-center mb-4">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item"><Link to="/purchase/purchase-orders">仕入伝票管理</Link></li>
            <li className="breadcrumb-item active">伝票詳細</li>
          </ol>
          <h2 className="page-header mb-0 fw-bold">発注伝票: <span className="text-primary">{po.order_number}</span></h2>
        </div>
        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-white fw-bold shadow-sm" onClick={() => navigate('/purchase/purchase-orders')}>
            <i className="fa fa-arrow-left me-1"></i> 一覧へ戻る
          </button>
        </div>
      </div>

      <div className="row gx-4">
        {/* 左側：発注明細 */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom pt-4 pb-3">
              <h5 className="mb-0 fw-bold d-flex align-items-center">
                <i className="fa fa-list-ul text-primary me-2"></i>発注明細
                <span className="badge bg-light text-dark border ms-3 fw-normal fs-6">{po.items?.length || 0} 行</span>
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light text-muted small">
                    <tr>
                      <th className="ps-4">商品 / SKU</th>
                      <th className="text-end">発注単価</th>
                      <th className="text-end">発注数量</th>
                      <th className="text-end pe-4">小計</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.items && po.items.length > 0 ? po.items.map(item => {
                      const sku = item.master_variant;
                      const product = sku?.master_product;
                      return (
                        <tr key={item.uuid}>
                          <td className="ps-4 py-3">
                            <div className="d-flex align-items-center">
                              {/* 🌟 画像の表示条件を親商品の「代表画像」に切り替え！ */}
                              <div className="bg-light border overflow-hidden shadow-sm rounded me-3" style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {product?.image_url ? (
                                  <img src={product.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <i className="fa fa-image fs-5 text-muted opacity-3"></i>
                                )}
                              </div>
                              <div>
                                {product?.uuid ? (
                                  <Link
                                    to={`/purchase/products/edit/${product.uuid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="fw-bold fs-14px font-monospace text-primary text-decoration-none"
                                    title="商品マスター詳細を別タブで開く"
                                  >
                                    {sku?.sku_code || '不明なSKU'} <i className="fa fa-external-link-alt ms-1 opacity-75" style={{ fontSize: '0.75rem' }}></i>
                                  </Link>
                                ) : (
                                  <div className="fw-bold fs-14px font-monospace text-dark">{sku?.sku_code || '不明なSKU'}</div>
                                )}
                                <div className="small text-muted mt-1">{product?.name || '--'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-end font-monospace">¥{Number(item.unit_price).toLocaleString()}</td>
                          <td className="text-end fw-bold fs-5 text-indigo">{item.quantity}</td>
                          <td className="text-end pe-4 fw-bold font-monospace">¥{(Number(item.unit_price) * item.quantity).toLocaleString()}</td>
                        </tr>
                      )
                    }) : (
                      <tr><td colSpan="4" className="text-center py-5 text-muted">明細がありません</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* 右側：伝票情報とアクション */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom pt-4 pb-3">
              <h5 className="mb-0 fw-bold"><i className="fa fa-info-circle text-info me-2"></i>伝票ステータス</h5>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                <span className="text-muted fw-bold small">現在の状態</span>
                {getStatusBadge(po.status)}
              </div>
              <div className="mb-3">
                <label className="text-muted small fw-bold mb-1">仕入先</label>
                <div className="fw-bold fs-14px">{getSupplierName(po.supplier_uuid)}</div>
              </div>
              <div className="mb-3 p-2 bg-light rounded border border-secondary border-opacity-25">
                <label className="text-muted small fw-bold mb-1"><i className="fa fa-hashtag me-1"></i>メーカー伝票番号 (先方注文番号)</label>
                <div className="fw-bold fs-14px font-monospace text-primary">
                  {po.supplier_order_number ? po.supplier_order_number : <span className="text-muted opacity-50">未登録</span>}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-6">
                  <label className="text-muted small fw-bold mb-1">発注日</label>
                  <div className="font-monospace">{new Date(po.order_date).toLocaleDateString('ja-JP')}</div>
                </div>
                <div className="col-6">
                  <label className="text-muted small fw-bold mb-1">完了（入庫）日</label>
                  <div className="font-monospace">{po.completed_at ? new Date(po.completed_at).toLocaleDateString('ja-JP') : '--'}</div>
                </div>
              </div>
              <div className="mb-3">
                <label className="text-muted small fw-bold mb-1">社内メモ</label>
                <div className="p-3 bg-light rounded small text-dark" style={{ minHeight: '60px' }}>
                  {po.note || <span className="text-muted opacity-5">メモはありません</span>}
                </div>
              </div>
            </div>
            <div className="card-footer bg-light p-4">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold text-muted">発注合計金額</span>
                <span className="fs-3 fw-bold text-dark font-monospace">¥{Number(po.total_amount).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3"><i className="fa fa-bolt text-warning me-2"></i>アクション</h6>

              {po.status === 'draft' && (
                <button className="btn btn-primary w-100 fw-bold py-2 mb-2 shadow-sm text-white">
                  <i className="fa fa-paper-plane me-2"></i>発注を確定する
                </button>
              )}

              {po.status === 'ordered' && (
                <button className="btn btn-success w-100 fw-bold py-2 mb-2 shadow-sm">
                  <i className="fa fa-box-open me-2"></i>商品を受け取り、入庫を完了する
                </button>
              )}

              {(po.status === 'draft' || po.status === 'ordered') && (
                <button className="btn btn-outline-danger w-100 fw-bold py-2">
                  <i className="fa fa-ban me-2"></i>伝票をキャンセル
                </button>
              )}

              {po.status === 'completed' && (
                <div className="alert alert-success border-0 small fw-bold text-center mb-0">
                  <i className="fa fa-check-circle me-1"></i>この伝票の在庫は反映済みです
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderDetail;